import { Popup } from '@paywithextend/components'
import {
  ConnectionCallbackArgs,
  ErrorCallbackArgs,
} from 'https://link-sdk.codat.io'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { EmbeddedCodatLink } from 'src/app/CreateOrgIntegration/EmbeddedCodatLink'
import {
  createOrgConnectionIntent,
  createOrgIntegration,
} from 'src/app/CreateOrgIntegration/redux/createOrgIntegration.actions'
import {
  selectConnectionIntentFromState,
  selectCreateOrgIntegrationErrorFromState,
  selectCreateOrgIntegrationLoadingFromState,
  selectOrgIntegrationFromState,
} from 'src/app/CreateOrgIntegration/redux/createOrgIntegration.selectors'
import LoadingPopupContent from 'src/components/LoadingPopupContent/LoadingPopupContent'
import { OrganizationConnectedPlatform } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { closeModal, connectedReplace } from 'src/navigation/Lnk'
import { manageOrgIntegrationUrl } from 'src/navigation/urls'
import { createSetIsCreateOrgIntegrationOpen } from 'src/redux/actions/app.actions'
import { getOrganizations } from 'src/redux/actions/data/organizations.actions'
import { selectCreateOrgIntegrationIntegrationTypeFromState } from 'src/redux/selectors/app.selectors'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import { css } from 'styled-components'

const CreateOrgIntegration: React.FC<
  React.PropsWithChildren
> = (): React.ReactElement => {
  const dispatch = useFlyoutDispatch()
  const loading = useSelector(selectCreateOrgIntegrationLoadingFromState)
  const error = useSelector(selectCreateOrgIntegrationErrorFromState)
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const connectionIntent = useSelector(selectConnectionIntentFromState)
  const orgIntegration = useSelector(selectOrgIntegrationFromState)
  const integrationType = useSelector(
    selectCreateOrgIntegrationIntegrationTypeFromState,
  )

  const history = useHistory()

  useEffect(() => {
    return history.listen(() => {
      if (history.action === 'POP') {
        dispatch(createSetIsCreateOrgIntegrationOpen(false))
      }
    })
  }, [])

  useEffect((): void => {
    dispatch(createOrgConnectionIntent(organizationId, integrationType))
  }, [])

  useEffect((): void => {
    if (error) {
      closePopup()
    }
  }, [error])

  useEffect((): void => {
    if (orgIntegration === undefined) {
      return
    }
    closePopup()
    // at this point we need to refresh the in memory organizations to reflect new integration
    dispatch(getOrganizations())
    dispatch(
      connectedReplace(
        manageOrgIntegrationUrl(
          orgIntegration.organizationId,
          orgIntegration.id,
        ),
      ),
    )
  }, [orgIntegration])

  const closePopup = (): void => {
    dispatch(createSetIsCreateOrgIntegrationOpen(false))
    dispatch(closeModal())
  }

  const handleFinishedAuthentication = (): void => {
    closePopup()
  }

  const handleAuthenticationSuccess = (
    connection: ConnectionCallbackArgs,
  ): void => {
    if (connectionIntent === undefined) {
      return
    }
    dispatch(
      createOrgIntegration(organizationId, {
        connectionIntentId: connectionIntent.id,
        connectionToken: connection.connectionId,
        connectedPlatform: OrganizationConnectedPlatform.NETSUITE,
      }),
    )
  }

  return (
    <Popup
      css={css`
        display: flex;
        flex-direction: column;
        position: relative;
        background: none;
        border-radius: 0px;
        box-shadow: none;
        width: auto;
        padding: 60px;
        #app-link {
          [class*='Typography_h'] {
            font-size: 24px !important;
            line-height: 1.5em;
          }
        }
      `}
    >
      {connectionIntent === undefined || loading ? (
        <LoadingPopupContent />
      ) : (
        <EmbeddedCodatLink
          companyId={connectionIntent.companyToken}
          onConnection={(connection: ConnectionCallbackArgs): void =>
            handleAuthenticationSuccess(connection)
          }
          onClose={(): void => closePopup()}
          onError={(err: ErrorCallbackArgs): void => alert('Error: ' + err)}
          onFinish={(): void => handleFinishedAuthentication()}
        />
      )}
    </Popup>
  )
}

export default CreateOrgIntegration
